import React, { createContext, useContext, useReducer, useEffect } from 'react';
import { authApi } from '../services/api';

// État initial
const initialState = {
    user: null,
    isAuthenticated: false,
    isLoading: false,
    // True only during the initial auth check on mount; drives the full-page
    // loader. Login/register use isLoading instead so they don't unmount the page.
    isInitializing: true,
    error: null
};

// Actions
const AuthActions = {
    SET_LOADING: 'SET_LOADING',
    LOGIN_SUCCESS: 'LOGIN_SUCCESS',
    LOGOUT: 'LOGOUT',
    SET_ERROR: 'SET_ERROR',
    CLEAR_ERROR: 'CLEAR_ERROR',
    UPDATE_USER: 'UPDATE_USER'
};

// Reducer
const authReducer = (state, action) => {
    switch (action.type) {
        case AuthActions.SET_LOADING:
            return {
                ...state,
                isLoading: action.payload,
                error: null
            };

        case AuthActions.LOGIN_SUCCESS:
            return {
                ...state,
                user: action.payload.user,
                isAuthenticated: true,
                isLoading: false,
                isInitializing: false,
                error: null
            };

        case AuthActions.LOGOUT:
            return {
                ...state,
                user: null,
                isAuthenticated: false,
                isLoading: false,
                isInitializing: false,
                error: null
            };

        case AuthActions.SET_ERROR:
            return {
                ...state,
                error: action.payload,
                isLoading: false
            };

        case AuthActions.CLEAR_ERROR:
            return {
                ...state,
                error: null
            };

        case AuthActions.UPDATE_USER:
            return {
                ...state,
                user: { ...state.user, ...action.payload }
            };

        default:
            return state;
    }
};

// Contexte
const AuthContext = createContext();

// Hook pour utiliser le contexte
export const useAuth = () => {
    const context = useContext(AuthContext);
    if (!context) {
        throw new Error('useAuth doit être utilisé dans un AuthProvider');
    }
    return context;
};

// Applique le thème d'interface : 'glass' (défaut) ou 'legacy' (ancien thème sombre plat).
// La classe sur <html> pilote les surcharges CSS de index.css (.theme-legacy).
const applyTheme = (theme) => {
    document.documentElement.classList.toggle('theme-legacy', theme === 'legacy');
};

// Provider
export const AuthProvider = ({ children }) => {
    const [state, dispatch] = useReducer(authReducer, initialState);

    // Le thème suit l'utilisateur connecté ; pages publiques = thème par défaut
    useEffect(() => {
        applyTheme(state.user?.theme || 'glass');
    }, [state.user?.theme]);

    // Vérifier l'authentification au chargement
    useEffect(() => {
        const initAuth = async () => {
            dispatch({ type: AuthActions.SET_LOADING, payload: true });

            // Si un access token local est présent, tenter getProfile (apiRequest
            // gère le retry via /auth/refresh si le token est expiré).
            if (authApi.isAuthenticated()) {
                try {
                    const user = await authApi.getProfile();
                    dispatch({ type: AuthActions.LOGIN_SUCCESS, payload: { user } });
                    return;
                } catch (error) {
                    console.warn('getProfile a échoué, tentative de refresh direct:', error);
                }
            }

            // Pas de token local, ou getProfile a échoué : tenter le refresh via
            // le cookie httpOnly (valide 7 jours), qui survit à un reload.
            try {
                const refreshData = await authApi.refreshToken();
                dispatch({ type: AuthActions.LOGIN_SUCCESS, payload: { user: refreshData.user } });
            } catch (refreshError) {
                authApi.clearAuth();
                dispatch({ type: AuthActions.LOGOUT });
            }
        };

        initAuth();
    }, []);

    // Fonctions d'authentification
    const login = async (credentials) => {
        try {
            dispatch({ type: AuthActions.SET_LOADING, payload: true });
            dispatch({ type: AuthActions.CLEAR_ERROR });

            const data = await authApi.login(credentials);
            
            dispatch({ 
                type: AuthActions.LOGIN_SUCCESS, 
                payload: { user: data.user } 
            });

            return { success: true, user: data.user };
        } catch (error) {
            const errorMessage = error.message || 'Erreur lors de la connexion';
            dispatch({ type: AuthActions.SET_ERROR, payload: errorMessage });
            // status 403 = compte non activé (cf. login backend) → permet d'afficher le renvoi d'activation
            return { success: false, error: errorMessage, needsActivation: error.status === 403 };
        }
    };

    const register = async (userData) => {
        try {
            dispatch({ type: AuthActions.SET_LOADING, payload: true });
            dispatch({ type: AuthActions.CLEAR_ERROR });

            await authApi.register(userData);

            // Pas d'auto-login : le compte doit être activé par email avant connexion.
            dispatch({ type: AuthActions.SET_LOADING, payload: false });

            return { success: true };
        } catch (error) {
            const errorMessage = error.message || 'Erreur lors de l\'inscription';
            dispatch({ type: AuthActions.SET_ERROR, payload: errorMessage });
            return { success: false, error: errorMessage };
        }
    };

    const logout = async () => {
        try {
            await authApi.logout();
        } catch (error) {
            console.warn('Erreur lors de la déconnexion:', error);
        } finally {
            dispatch({ type: AuthActions.LOGOUT });
        }
    };

    const updateProfile = async (profileData) => {
        try {
            dispatch({ type: AuthActions.CLEAR_ERROR });
            
            const updatedUser = await authApi.updateProfile(profileData);
            
            dispatch({ 
                type: AuthActions.UPDATE_USER, 
                payload: updatedUser 
            });

            return { success: true, user: updatedUser };
        } catch (error) {
            const errorMessage = error.message || 'Erreur lors de la mise à jour du profil';
            dispatch({ type: AuthActions.SET_ERROR, payload: errorMessage });
            return { success: false, error: errorMessage };
        }
    };

    const updateTheme = async (theme) => {
        try {
            const updatedUser = await authApi.updateTheme({ theme });
            dispatch({ type: AuthActions.UPDATE_USER, payload: updatedUser });
            return { success: true };
        } catch (error) {
            const errorMessage = error.message || 'Erreur lors du changement de thème';
            dispatch({ type: AuthActions.SET_ERROR, payload: errorMessage });
            return { success: false, error: errorMessage };
        }
    };

    const dismissNotice = async (noticeId) => {
        // Marque la notice comme vue côté serveur ; l'échec est silencieux
        // (la notice réapparaîtra au pire à la prochaine connexion).
        dispatch({
            type: AuthActions.UPDATE_USER,
            payload: { dismissed_notices: [...(state.user?.dismissed_notices || []), noticeId] }
        });
        try {
            await authApi.dismissNotice(noticeId);
        } catch (error) {
            console.warn('Impossible de marquer la notice comme vue:', error);
        }
    };

    const changePassword = async (passwordData) => {
        try {
            dispatch({ type: AuthActions.CLEAR_ERROR });
            
            await authApi.changePassword(passwordData);
            
            return { success: true };
        } catch (error) {
            const errorMessage = error.message || 'Erreur lors du changement de mot de passe';
            dispatch({ type: AuthActions.SET_ERROR, payload: errorMessage });
            return { success: false, error: errorMessage };
        }
    };

    const clearError = () => {
        dispatch({ type: AuthActions.CLEAR_ERROR });
    };

    // Valeurs du contexte
    const value = {
        // État
        user: state.user,
        isAuthenticated: state.isAuthenticated,
        isLoading: state.isLoading,
        isInitializing: state.isInitializing,
        error: state.error,

        // Actions
        login,
        register,
        logout,
        updateProfile,
        updateTheme,
        dismissNotice,
        changePassword,
        clearError,

        // Utilitaires
        getUserType: () => state.user?.user_type,
        getUserName: () => state.user?.name,
        getUserEmail: () => state.user?.email,
        isClient: () => state.user?.user_type === 'client',
        isProsthetist: () => state.user?.user_type === 'prosthetist',
        isAdmin: () => state.user?.user_type === 'admin'
    };

    return (
        <AuthContext.Provider value={value}>
            {children}
        </AuthContext.Provider>
    );
};

export default AuthContext;